interface TDDDocumentHeaderProps {
  tddId: string;
  title: string;
  lastReview?: string;
  documentStatus?: string;
  scope?: string;
}

import { useRef, useEffect } from "react";
import { useParallax } from "../../hooks/useParallax";

const statusLabels: Record<string, string> = {
  "spec-ready": "SPEC READY",
  "in-development": "IN DEVELOPMENT",
  "planned": "PLANNED",
  "deployed": "DEPLOYED",
};

export function TDDDocumentHeader({
  tddId,
  title,
  documentStatus,
  scope,
}: TDDDocumentHeaderProps) {
  // Watermark parallax: 1-3px movement
  const watermarkParallax = useParallax(0.015);
  
  return (
    <div className="mb-6 space-y-6 relative">
      {/* Technical Watermark - Design.md: opacity 4-6%, parallax */}
      <div 
        className="absolute -z-10 opacity-[0.05] pointer-events-none"
        style={{
          transform: `translateY(${watermarkParallax}px) scale(1.01)`,
          transition: "transform 0.1s ease-out",
        }}
      >
        {/* Engineering diagram - node-link graph style */}
        <svg width="400" height="200" viewBox="0 0 400 200" className="text-[rgba(24,224,200,1)]">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" opacity="0.3"/>
            </pattern>
          </defs>
          <rect width="400" height="200" fill="url(#grid)" />
          {/* Node-link graph */}
          <circle cx="100" cy="100" r="8" fill="currentColor" opacity="0.4" />
          <circle cx="200" cy="80" r="8" fill="currentColor" opacity="0.4" />
          <circle cx="300" cy="120" r="8" fill="currentColor" opacity="0.4" />
          <circle cx="150" cy="150" r="8" fill="currentColor" opacity="0.4" />
          <line x1="100" y1="100" x2="200" y2="80" stroke="currentColor" strokeWidth="1" opacity="0.3" />
          <line x1="200" y1="80" x2="300" y2="120" stroke="currentColor" strokeWidth="1" opacity="0.3" />
          <line x1="100" y1="100" x2="150" y2="150" stroke="currentColor" strokeWidth="1" opacity="0.3" />
        </svg>
      </div>

      {/* Metadata Header Block - Design.md format */}
      <div className="space-y-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-white/55">
          {tddId} • TECHNICAL SPEC 
        </p>
        {documentStatus && (
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-white/55">
            DOCUMENT STATUS: {statusLabels[documentStatus] || documentStatus.toUpperCase()}
          </p>
        )}
        {scope && (
          <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-green-500">
            SCOPE: {scope}
          </p>
        )}
      </div>

      {/* Strong divider line below metadata - Design.md */}
      <div className="h-[1px] w-full bg-white/20" />

      {/* Title with breathing room */}
      <div className="space-y-4">
        <h2 className="font-serif text-2xl sm:text-3xl md:text-[42px] lg:text-[48px] font-semibold leading-[1.1] tracking-[0.3px] text-white max-w-full">
          {title}
        </h2>
      </div>
    </div>
  );
}
